import React, { Fragment, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Form, InputNumber, message, Row, Select, Space, Spin, Table, Typography } from 'antd';
import PropTypes from 'prop-types';
import { commissionBracketsType } from '../../constants/global';
import {
	getactionMessages,
	getCommissionMessages,
	getFields,
	getModalMessages,
	getPayrollMessages,
} from '../../constants/messages';
import { searchInString } from '../../utilities/util';
import {
	SaveCommissionPercentConfig,
	SaveCommissionProductsConfig,
	SaveCustomPriceCommissionProductsConfig,
} from './actions';
import EditCommissionBracketForm from './EditCommissionBracketForm';

let commissionMessages = getCommissionMessages(),
	actionMessages = getactionMessages(),
	modalMessages = getModalMessages(),
	payrollsMessages = getPayrollMessages(),
	fieldsMessages = getFields();

const messages = {
	...commissionMessages,
	...actionMessages,
	...modalMessages,
	...payrollsMessages,
	...fieldsMessages,
};

const SaleConfigurations = ({
	products,
	productsSales,
	commissionBrackets,
	setCommissionBrackets,
	commissionPercent,
	customPriceProductsSales,
}) => {
	const intl = useIntl();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [showEdit, setShowEdit] = useState(false);
	const [bracketToEdit, setbracketToEdit] = useState({});

	const productOptions = products.map((product) => ({
		key: product.id,
		value: product.id,
		label: `${product.code} - ${product.description}`,
	}));

	const handleSave = async () => {
		try {
			setLoading(true);
			const values = await form.validateFields();
			await SaveCommissionPercentConfig(commissionPercent.id, values.commissionPercent);
			await SaveCommissionProductsConfig(productsSales.id, values.commissionableProducts, productsSales.value);
			await SaveCustomPriceCommissionProductsConfig(
				customPriceProductsSales.id,
				values.commissionCustomPrice,
				customPriceProductsSales.value
			);
			message.success(intl.formatMessage({ ...messages.saveConfigurationsSuccess }));
		} catch (error) {
			message.error(intl.formatMessage({ ...messages.saveConfigurationsError }));
		} finally {
			setLoading(false);
		}
	};

	const openEditModal = (bracket) => {
		setbracketToEdit(bracket);
		setShowEdit(true);
	};

	const tableActions = (record) => {
		return (
			<Typography.Link className="editable-add-btn" onClick={() => openEditModal(record)}>
				<EditOutlined />
				<FormattedMessage {...messages.edit} />
			</Typography.Link>
		);
	};

	const columns = [
		{
			title: intl.formatMessage({ ...messages.commissionRangeService }),
			children: [
				{
					title: intl.formatMessage({ ...messages.minAmount }),
					dataIndex: 'min',
					key: 'min',
				},
				{
					title: intl.formatMessage({ ...messages.maxAmount }),
					dataIndex: 'max',
					key: 'max',
				},
			],
		},
		{
			title: intl.formatMessage({ ...messages.multiplier }),
			dataIndex: 'multiplier',
			key: 'multiplier',
		},
		{
			title: intl.formatMessage({ ...messages.actions }),
			dataIndex: 'id',
			render: (id, record, index) => tableActions({ ...record, index }),
		},
	];

	return (
		<Fragment>
			<Spin spinning={loading}>
				<Form
					form={form}
					layout="vertical"
					initialValues={{
						commissionPercent: commissionPercent.value * 100,
						commissionableProducts: productsSales.value ? productsSales.value : null,
						commissionCustomPrice: customPriceProductsSales.value ? customPriceProductsSales.value : null,
					}}
				>
					<Space direction="vertical" size="large" style={{ display: 'flex' }}>
						<Table
							bordered
							size="small"
							columns={columns}
							dataSource={commissionBrackets}
							pagination={false}
						/>
						<Form.Item
							name="commissionPercent"
							label={intl.formatMessage({ ...messages.commissionPercent })}
						>
							<InputNumber
								min={0}
								max={100}
								precision={0}
								className="job-full-component"
								formatter={(value) => `${value}%`}
								parser={(value) => value.replace('%', '')}
							/>
						</Form.Item>
					</Space>
					<Form.Item
						name="commissionableProducts"
						label={intl.formatMessage({ ...messages.commissionableProducts })}
					>
						<Select
							showSearch
							mode="multiple"
							filterOption={(input, option) => searchInString(input, option.label)}
							options={productOptions}
						/>
					</Form.Item>
					<Form.Item
						name="commissionCustomPrice"
						label={intl.formatMessage({ ...messages.commissionCustomPriceProducts })}
					>
						<Select
							showSearch
							mode="multiple"
							filterOption={(input, option) => searchInString(input, option.label)}
							options={productOptions}
						/>
					</Form.Item>
				</Form>
				<Row justify="end">
					<Button type="primary" onClick={() => handleSave()} icon={<SaveOutlined />}>
						{''}
						<FormattedMessage {...messages.save} />
					</Button>
				</Row>
			</Spin>
			<EditCommissionBracketForm
				bracket={bracketToEdit}
				handleCancel={() => {
					setbracketToEdit({});
					setShowEdit(false);
				}}
				commissionBrackets={commissionBrackets}
				setCommissionBrackets={setCommissionBrackets}
				showModal={showEdit}
				bracketType={commissionBracketsType.SALES}
			/>
		</Fragment>
	);
};

SaleConfigurations.propTypes = {
	products: PropTypes.array.isRequired,
	productsSales: PropTypes.object.isRequired,
	commissionBrackets: PropTypes.array.isRequired,
	setCommissionBrackets: PropTypes.func.isRequired,
	commissionPercent: PropTypes.object.isRequired,
	customPriceProductsSales: PropTypes.object.isRequired,
};

export default SaleConfigurations;
